import { createSelector } from 'reselect';
import { fromJS } from 'immutable';
import {
  ROUTE_ACCOUNTS_LOGIN,
  ROUTE_ACCOUNTS_SIGNUP,
  ROUTE_HOMEPAGE,
} from '../../routeActions';
import { selectLocation, selectQueryObject } from '../../location/selectors';
import { selectCurrentLocation } from '../../Navigation/selectors';
import { getActionForUrl } from '../../../web/utilities/reduxLinks';
import { currentUserIdSelector } from '../selectors';
import { LoginMode } from './constants';

export const selectLoginState = createSelector(selectLocation, (location) => {
  switch (location.get('type')) {
    case ROUTE_ACCOUNTS_LOGIN: {
      return fromJS({
        action: 'login',
        postLoginRedirectUrl: location.getIn(['query', 'redirectTo']),
      });
    }
    case ROUTE_ACCOUNTS_SIGNUP: {
      return fromJS({
        action: 'signup',
        postLoginRedirectUrl: location.getIn(['query', 'redirectTo']),
      });
    }
    default: {
      return fromJS({
        action: null,
        postLoginRedirectUrl: null,
      }); // A routing action for another page
    }
  }
});

export const selectIsOnLoginPage = createSelector(selectLoginState, (login) => {
  return login.get('action') === 'login';
});

export const selectIsOnSignupPage = createSelector(
  selectLoginState,
  (login) => {
    return login.get('action') === 'signup';
  }
);

export const selectPostLoginRedirectUrl = createSelector(
  selectLoginState,
  (login) => {
    return login.get('postLoginRedirectUrl');
  }
);

export const selectAuthLinkQuery = createSelector(
  selectPostLoginRedirectUrl,
  selectIsOnLoginPage,
  selectIsOnSignupPage,
  currentUserIdSelector,
  selectCurrentLocation,
  (
    postLoginRedirectUrl,
    isOnLoginPage,
    isOnSignupPage,
    currentUserId,
    currentLocation
  ) => {
    let force;
    if (isOnLoginPage || isOnSignupPage || currentUserId) {
      force = true;
    } else {
      force = false;
    }
    return {
      force,
      redirectTo: postLoginRedirectUrl || currentLocation,
    };
  }
);

export const selectLoginLink = createSelector(selectAuthLinkQuery, (query) => ({
  type: ROUTE_ACCOUNTS_LOGIN,
  query,
}));

export const selectSignupLink = createSelector(
  selectAuthLinkQuery,
  (query) => ({
    type: ROUTE_ACCOUNTS_LOGIN,
    query: {
      ...query,
      mode: LoginMode.signup,
    },
    view: 'signup',
  })
);

export const selectForceDisplayLoginSignup = createSelector(
  selectLocation,
  (location) => {
    const force = location.getIn(['query', 'force']);
    return force === '1' || force === 'true';
  }
);

export const selectPostLoginRedirectAction = createSelector(
  selectQueryObject,
  (query) => {
    const { redirectTo, mode } = query;
    let action = { type: ROUTE_HOMEPAGE };

    if (redirectTo) {
      const redirectToAction = getActionForUrl(redirectTo);
      if (redirectToAction && redirectToAction.type !== 'NOT_FOUND') {
        action = redirectToAction;
      }
    }

    let excludedKeys = ['force', 'redirectTo', 'verificationCode'];
    if (mode && mode === 'signup') {
      excludedKeys = ['force', 'redirectTo', 'verificationCode', 'mode'];
    }

    const queryParams = Object.keys(query)
      .filter((key) => !excludedKeys.includes(key))
      .reduce((obj, key) => ({ ...obj, [key]: query[key] }), {});

    return {
      ...action,
      query: {
        ...action.query,
        ...queryParams,
      },
    };
  }
);
